import { Button } from "components/atoms/button/button";
import { ButtonSize } from "components/atoms/button/button.types";
import { InfoIcon } from "components/icons/info";
import Image from "next/legacy/image";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { colors } from "styles/constants/colors";
import { getDayMap, noop } from "utils/utils";
import { ModalSlotConfirmation } from "./components/modal-slot-confirmation/modal-slot-confirmation";
import {
  renderAvailableSeatsChip,
  renderClassStatusChip,
  SlotCard
} from "./components/slot-card/slot-card";
import {
  AccordionContentWrapper,
  AccordionSubTitle,
  AccordionTitle,
  AccordionTitleContainer,
  ArrowRight,
  BanerSubTitle,
  BannerContainer,
  BannerTitle,
  ButtonContentWrapper,
  ChooseSlotTitle,
  ContinueText,
  ContinueTextWrapper,
  Divider,
  FormWrapper,
  HeaderWrapper,
  HelpButtonText,
  HelpButtonWrapper,
  ImageConatainer,
  PageContainer,
  SkipToJadwalLink,
  SlotContainer,
  SlotDateAndTime,
  SlotStartDateAndSeatCapacity,
  SlotWrapper,
  StepText,
  TeacherName,
  Ticker
} from "./slot-selection.styles";
import { IModal } from "./slot-selection.types";
import { useSlotSelection } from "./use-slot-selection";
import { Accordion } from "components/accordion/accordion";
import { CheckCircle } from "components/icons/check-circle";
import FaqIcon from "public/assets/slot-selection/faq_question.svg";
import { KelasLiveCMBAckModal } from "components/kelas-live/kelas-live-call-me-back/kelas-live-cmb-ack-modal/kelas-live-cmb-ack-modal";
import { ModalConfirmationCallMeBack } from "./components/modal-confirmation-call-me-back/modal-confirmation-call-me-back";
import JadwalChangesInfoFromFeature from "components/jadwal/jadwal-changes-info/jadwal-changes-info-from-feature";

interface Props {
  isEligibleToSkip: boolean;
}

const SlotSelection: React.FC<Props> = ({ isEligibleToSkip }) => {
  const {
    fullName,
    stepLength,
    step,
    sectionsData,
    isLoading,
    selectedSection,
    setSelectedSection,
    handleClickSection,
    progressRef,
    handleNextSection,
    handleSelectSlot,
    getOrderedSlots,
    getClashesSlot,
    formatTime,
    modal,
    handleCloseModal,
    handleFinishModalConfirmation,
    isDisabledNextButton,
    phoneNumber,
    primaryUser,
    handleClickHelp,
    setModal,
    filteredSections,
    handleSlotSelectionSkip
  } = useSlotSelection({ isEligibleToSkip });

  return (
    <PageContainer>
      <ModalConfirmationCallMeBack
        isOpen={modal === IModal.CALL_ME_BACK_CONFIRMATION}
        onClose={() => setModal("")}
        onSubmitCallMe={() => setModal(IModal.CALL_ME_BACK)}
      />
      {modal === IModal.CALL_ME_BACK && (
        <KelasLiveCMBAckModal
          onClose={() => setModal("")}
          handleOnClick={() => setModal("")}
          phoneNumber={phoneNumber}
          primary_user={primaryUser}
          onGoingClassExists={false}
          isShowingCallDifferentNumber={false}
        />
      )}
      <ModalSlotConfirmation
        isOpen={modal === IModal.CONFIRMATION}
        sections={sectionsData}
        onClose={filteredSections.length === 0 ? noop : handleCloseModal}
        onFinish={handleFinishModalConfirmation}
      />
      <BannerContainer>
        <BannerTitle>Hi, {fullName}!</BannerTitle>
        <BanerSubTitle>
          Pilih jadwal dulu yuk biar bisa <br /> masuk kelas.
        </BanerSubTitle>
        <ImageConatainer>
          <Image
            objectFit="contain"
            src="/assets/slot-selection/coco-select-slot.png"
            layout="fill"
            alt="coco-select-slot.png"
          />
        </ImageConatainer>
      </BannerContainer>

      <SlotContainer>
        <FormWrapper>
          <div>
            <HeaderWrapper>
              <ChooseSlotTitle>Pilih jadwal kelasmu 🗓️</ChooseSlotTitle>
              <HelpButtonWrapper onClick={handleClickHelp}>
                <Image
                  src={FaqIcon}
                  width={20}
                  height={20}
                  alt="faq-icon.svg"
                />
                <HelpButtonText>Bantuan</HelpButtonText>
              </HelpButtonWrapper>
            </HeaderWrapper>
            <Ticker>
              <InfoIcon color={colors.brand["500"]} width="13" height="13" />
              Kalo nanti jadwalnya nggak cocok, bisa diganti lewat Kakak Siaga
            </Ticker>

            <SlotWrapper>
              <JadwalChangesInfoFromFeature />
              {isLoading ? (
                <>
                  <Skeleton width="50%" />
                  {Array.from({ length: 3 }).map((_, key) => (
                    <Skeleton key={key} height={72} />
                  ))}
                </>
              ) : (
                <>
                  {filteredSections.map((section, sectionKey) => (
                    <div key={sectionKey}>
                      {sectionKey < progressRef && (
                        <Accordion
                          title={
                            <AccordionTitleContainer>
                              {step !== sectionKey + 1 &&
                              section.selected_slot?.slot ? (
                                <>
                                  <AccordionTitle>
                                    {section.title}
                                  </AccordionTitle>
                                  <CheckCircle
                                    color={colors.green["800"]}
                                    width="16"
                                  />
                                </>
                              ) : (
                                <>{section.title}</>
                              )}
                            </AccordionTitleContainer>
                          }
                          onClick={(isOpen) => {
                            if (isOpen) {
                              setSelectedSection(sectionKey + 1);
                            }
                            handleClickSection(sectionKey + 1);
                          }}
                          collapsed={selectedSection === sectionKey + 1}
                          isDisabled={step - 1 === sectionKey}
                          subTitle={
                            section.is_optional || step - 1 === sectionKey ? (
                              ""
                            ) : (
                              <AccordionSubTitle>
                                <SlotDateAndTime>
                                  {section.selected_slot?.formatted_schedule}
                                </SlotDateAndTime>
                                <SlotStartDateAndSeatCapacity>
                                  {section.selected_slot
                                    ? renderClassStatusChip(
                                        section.selected_slot.slot.start_date
                                      )
                                    : ""}
                                  {section.selected_slot
                                    ? renderAvailableSeatsChip(
                                        section.selected_slot.slot
                                          .remaining_seats
                                      )
                                    : ""}

                                  {section.selected_slot?.slot.teacher ? (
                                    <>
                                      <Divider />
                                      <TeacherName>
                                        Guru Juara :{" "}
                                        {
                                          section.selected_slot?.slot.teacher
                                            .name
                                        }
                                      </TeacherName>
                                    </>
                                  ) : (
                                    ""
                                  )}
                                </SlotStartDateAndSeatCapacity>
                              </AccordionSubTitle>
                            )
                          }
                          content={
                            <AccordionContentWrapper>
                              {getOrderedSlots(section.slots).map(
                                (slot, slotKey) => (
                                  <SlotCard
                                    isSelected={
                                      section.selected_slot?.slot.id === slot.id
                                    }
                                    isOptional={section.is_optional}
                                    teacher={slot.teacher}
                                    clashesWith={getClashesSlot(slot, section)}
                                    key={slotKey}
                                    title={slot.name}
                                    days={slot.days
                                      .map((day) => getDayMap[day])
                                      .join(", ")}
                                    startTime={formatTime(slot.start_time)}
                                    endTime={formatTime(slot.end_time)}
                                    onSelectSlot={() =>
                                      handleSelectSlot(section.course_id, slot)
                                    }
                                    remainingSlots={slot.remaining_seats}
                                    startDate={slot.start_date}
                                  />
                                )
                              )}
                            </AccordionContentWrapper>
                          }
                        />
                      )}
                    </div>
                  ))}
                </>
              )}
            </SlotWrapper>
          </div>
          <div>
            <Button
              disabled={isDisabledNextButton()}
              isFullWidth
              variant="primary"
              size={ButtonSize.lg}
              onClick={handleNextSection}
            >
              <ButtonContentWrapper>
                <StepText>
                  Langkah {step}/{stepLength}
                </StepText>
                <ContinueTextWrapper>
                  <ContinueText>Lanjut</ContinueText>
                  <ArrowRight>
                    <Image
                      src="/assets/arrow-right.svg"
                      width="20"
                      height="20"
                      alt="arrow-right"
                    />
                  </ArrowRight>
                </ContinueTextWrapper>
              </ButtonContentWrapper>
            </Button>
            {isEligibleToSkip && (
              <SkipToJadwalLink href="/bimbel/user">
                <Button
                  isFullWidth
                  variant="link"
                  onClick={handleSlotSelectionSkip}
                >
                  Lewati pilih jadwal
                </Button>
              </SkipToJadwalLink>
            )}
          </div>
        </FormWrapper>
      </SlotContainer>
    </PageContainer>
  );
};

export { SlotSelection };
